// src/tutoriel/useTutoriel.js — L'ÉTAT DES PARCOURS GUIDÉS.
//
// Le contenu (chapitres, textes, ancres, conditions) vient du SERVEUR : ce
// module ne connaît aucune étape. Il sait où on en est, il navigue, il marque.
//
// LE TUTORIEL MONTRE, IL N'AGIT JAMAIS. Il ne clique sur aucun bouton de
// l'application et n'écrit rien d'autre que sa propre progression. C'est ce
// qui permet de le jouer sur les données RÉELLES de la personne sans rien
// créer chez un vrai client.
//
// ON NE BLOQUE JAMAIS UNE LECTURE SUR UNE ÉCRITURE : si marquer l'étape
// échoue, la bulle avance quand même (la personne a lu) et la prochaine
// écriture rattrapera — la route est un upsert.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import APIService from '../services/api';
import { elementAncre } from './ancres';
import { aplatir, dispoDepuis, premiereAFaire } from './navigation';

const BASE = 'https://besoins-tat-production.up.railway.app';
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const Ctx = createContext(null);
export const useTutoriel = () => useContext(Ctx) || { disponible: false };

// LE TIROIR NE SE RESTAURE PAS AU CHARGEMENT. Son état était retenu dans le
// stockage local, et n'y était remis à zéro qu'en le refermant par le bouton
// de la barre latérale : refermé par la croix ou par le fond, il rouvrait tout
// seul à chaque actualisation de la page (vécu 18.09 : « à chaque fois que
// j'actualise j'ai le truc de tutoriel »). Un panneau modal s'ouvre sur un
// geste — recharger une page n'en est pas un.
const PANNEAU_CLE = 'erpia.tuto.panneau';

export function TutorielProvider({ children, currentView, onNaviguer, actif: montable = true }) {
  const [disponible, setDisponible] = useState(false);
  const [charge, setCharge] = useState(false);
  const [vue, setVue] = useState(null);          // { propose, proposer_banniere, parcours[] }
  const [actif, setActif] = useState(null);      // { cle, chapitres, index }
  const [panneauOuvert, setPanneauOuvert] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);           // recalcul du mode quand le DOM bouge

  // ── Chargement initial ───────────────────────────────────────────────────
  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/tutoriels`, { headers: authH() });
      // Route absente : rien ne s'affiche, rien ne casse, rien n'est écrit.
      if ([404, 501].includes(r.status)) { setDisponible(false); setCharge(true); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setVue(j); setDisponible(true); setErreur(null);
    } catch (e) {
      // Le bouton reste, le panneau dira pourquoi — le reste de l'app intact.
      setDisponible(true); setErreur(e.message);
    } finally { setCharge(true); }
  }, []);

  useEffect(() => { if (montable) charger(); }, [montable, charger]);

  // La clé d'hier reste chez les gens : on l'efface une fois pour toutes,
  // sinon elle traîne sans que rien ne la lise plus.
  useEffect(() => {
    try { localStorage.removeItem(PANNEAU_CLE); } catch { /* navigation privée */ }
  }, []);

  // ── Le DOM bouge : l'ancre peut apparaître après un chargement ───────────
  useEffect(() => {
    if (!actif || typeof MutationObserver === 'undefined') return;
    let tId = null;
    const obs = new MutationObserver(() => {
      clearTimeout(tId);
      tId = setTimeout(() => setTick(t => t + 1), 150);
    });
    obs.observe(document.body, { childList: true, subtree: true });
    const surTaille = () => setTick(t => t + 1);
    window.addEventListener('resize', surTaille);
    return () => { clearTimeout(tId); obs.disconnect(); window.removeEventListener('resize', surTaille); };
  }, [actif]);

  const etapes = useMemo(() => aplatir(actif?.chapitres), [actif]);

  const etape = actif ? etapes[actif.index] || null : null;

  // LA BULLE NE S'ARRÊTE QUE SUR UNE ÉTAPE DISPONIBLE. Le panneau montre les
  // autres, grisées et avec leur raison — mais avancer d'un cran sur TOUTES
  // les étapes faisait atterrir la bulle sur « Le copilote » chez qui n'a pas
  // le chat, ou sur « Suivre les qualifications » chez un compte interne :
  // « Suivant » y était refusé par le serveur (409), et comme c'est la
  // dernière étape des deux parcours, « Terminer » restait planté sans rien
  // dire. La position et le total sont comptés sur le même périmètre que le
  // serveur (`progression.total`) : 3/16, pas 3/17 avec une étape fantôme.
  // La règle est dans ./navigation, testée à plat.
  const suivantDispo = actif ? dispoDepuis(etapes, actif.index, +1) : null;
  const precedentDispo = actif ? dispoDepuis(etapes, actif.index, -1) : null;
  const disponibles = useMemo(() => etapes.filter(e => e.disponible), [etapes]);
  const position = etape ? disponibles.indexOf(etape) + 1 : 0;

  // Pointée si son élément est là ET qu'on est sur le bon écran ; sinon
  // descriptive — même texte, plus la phrase qui dit comment y arriver.
  const surLeBonEcran = !etape?.ecran || currentView === etape.ecran
    || (etape.ecran === 'detail' && currentView === 'detail')
    || (etape.ecran === 'fiche_candidat' && currentView === 'candidats');
  const cible = useMemo(
    () => (surLeBonEcran && etape?.ancre ? elementAncre(etape.ancre) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [etape?.ancre, surLeBonEcran, tick]);
  const mode = cible ? 'pointe' : 'descriptif';

  useEffect(() => {
    if (!cible) return;
    try { cible.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* jsdom */ }
  }, [cible]);

  // ── Ouvrir / reprendre ───────────────────────────────────────────────────
  const ouvrir = useCallback(async (cle) => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/tutoriels/${cle}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      fetch(`${BASE}/tutoriels/${cle}`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ statut: 'en_cours' }) })
        .catch(() => { /* la lecture ne dépend pas de l'écriture */ });
      setActif({ cle, chapitres: j.chapitres, index: premiereAFaire(aplatir(j.chapitres)) });
      setPanneauOuvert(false);
      setErreur(null);
    } catch (e) { setErreur(e.message); }
    finally { setBusy(false); }
  }, []);

  // ── Avancer ──────────────────────────────────────────────────────────────
  const marquer = useCallback(async (statut) => {
    if (!actif || !etape) return;
    // On avance TOUT DE SUITE : la personne a lu. L'écriture suit.
    const dernier = suivantDispo === null;
    if (!dernier) setActif(a => a ? { ...a, index: suivantDispo } : a);
    else setPanneauOuvert(true);
    try {
      const r = await fetch(`${BASE}/tutoriels/${actif.cle}/etapes/${etape.cle}`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify({ statut }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setActif(a => a && a.cle === actif.cle
        ? { ...a, chapitres: a.chapitres.map(ch => ({ ...ch, etapes: ch.etapes.map(e => e.cle === etape.cle ? { ...e, statut } : e) })) }
        : a);
      if (dernier) { await charger(); setActif(null); }
    } catch (e) { setErreur(e.message); }
  }, [actif, etape, suivantDispo, charger]);

  const suivant = useCallback(() => marquer('faite'), [marquer]);
  const passer = useCallback(() => marquer('sautee'), [marquer]);
  const precedent = useCallback(() => {
    if (precedentDispo === null) return;
    setActif(a => a ? { ...a, index: precedentDispo } : a);
  }, [precedentDispo]);
  const allerA = useCallback((i) => setActif(a => a ? { ...a, index: i } : a), []);

  // Pause : on ferme, on n'écrit rien. Le parcours reste « en cours ».
  const pause = useCallback(() => { setActif(null); setPanneauOuvert(false); }, []);

  const emmener = useCallback(() => {
    if (etape?.chemin && onNaviguer) onNaviguer(etape.chemin);
  }, [etape, onNaviguer]);

  const geste = useCallback(async (cle, statut) => {
    try {
      await fetch(`${BASE}/tutoriels/${cle}`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ statut }) });
      await charger();
    } catch { /* silencieux : un refus de geste ne casse rien */ }
  }, [charger]);

  const ignorer = useCallback((cle) => geste(cle, 'ignore'), [geste]);

  // La bannière annonce parfois DEUX parcours (celui qui n'a pas de rôle
  // imposé les voit tous les deux) : on marque tout ce qu'elle annonce, et on
  // ne recharge qu'une fois.
  const banniereVue = useCallback(async (cles) => {
    const liste = [].concat(cles || []).filter(Boolean);
    if (!liste.length) return;
    try {
      await Promise.all(liste.map(cle => fetch(`${BASE}/tutoriels/${cle}`,
        { method: 'POST', headers: jsonH(), body: JSON.stringify({ statut: 'banniere_vue' }) })));
      await charger();
    } catch { /* silencieux : un refus de geste ne casse rien */ }
  }, [charger]);

  const recommencer = useCallback(async (cle) => {
    try {
      await fetch(`${BASE}/tutoriels/${cle}`, { method: 'DELETE', headers: authH() });
      await charger();
      await ouvrir(cle);
    } catch (e) { setErreur(e.message); }
  }, [charger, ouvrir]);

  const basculerPanneau = useCallback(() => setPanneauOuvert(o => !o), []);

  const valeur = {
    disponible, charge, erreur, busy,
    propose: vue?.propose || null,
    proposerBanniere: vue?.proposer_banniere === true,
    banniereParcours: vue?.banniere_parcours || [],
    parcoursDispo: vue?.parcours || [],
    actif: actif ? {
      ...actif, etapes, etape, mode, surLeBonEcran,
      position, total: disponibles.length,
      dernier: suivantDispo === null, aPrecedent: precedentDispo !== null,
    } : null,
    panneauOuvert, basculerPanneau, fermerPanneau: () => setPanneauOuvert(false),
    ouvrir, suivant, passer, precedent, allerA, pause, emmener,
    ignorer, banniereVue, recommencer, recharger: charger,
  };

  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export default useTutoriel;
